import { Component, input, output, signal, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ComponentSize } from '../types';

export interface MenuItem {
  label: string;
  route?: string;
  icon?: string;
  disabled?: boolean;
  badge?: string;
  children?: MenuItem[];
}

@Component({
  selector: 'app-menu',
  imports: [CommonModule, RouterModule],
  template: `
    <nav [class]="menuClasses" [attr.role]="'navigation'">
      <ul class="space-y-1">
        @for (item of items(); track item.label) {
          <li>
            @if (item.children && item.children.length > 0) {
              <div>
                <button
                  type="button"
                  [class]="getMenuItemClasses(item)"
                  (click)="toggleSubmenu(item)"
                  [attr.aria-expanded]="isSubmenuOpen(item)">
                  @if (item.icon) {
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                    </svg>
                  }
                  <span class="flex-1 text-left">{{ item.label }}</span>
                  @if (item.badge) {
                    <span class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {{ item.badge }}
                    </span>
                  }
                  <svg 
                    class="w-4 h-4 transition-transform duration-200"
                    [class.rotate-90]="isSubmenuOpen(item)"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                @if (isSubmenuOpen(item)) {
                  <ul class="ml-6 mt-1 space-y-1">
                    @for (child of item.children; track child.label) {
                      <li>
                        @if (child.route) {
                          <a
                            [routerLink]="child.route"
                            [class]="getSubmenuItemClasses(child)"
                            [routerLinkActive]="'active'">
                            {{ child.label }}
                          </a>
                        } @else {
                          <span [class]="getSubmenuItemClasses(child)">{{ child.label }}</span>
                        }
                      </li>
                    }
                  </ul>
                }
              </div>
            } @else {
              @if (item.route) {
                <a
                  [routerLink]="item.route"
                  [class]="getMenuItemClasses(item)"
                  [routerLinkActive]="'active'">
                  @if (item.icon) {
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                    </svg>
                  }
                  <span>{{ item.label }}</span>
                  @if (item.badge) {
                    <span class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {{ item.badge }}
                    </span>
                  }
                </a>
              } @else {
                <button
                  type="button"
                  [class]="getMenuItemClasses(item)"
                  (click)="onItemClick(item)">
                  @if (item.icon) {
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                    </svg>
                  }
                  <span>{{ item.label }}</span>
                  @if (item.badge) {
                    <span class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {{ item.badge }}
                    </span>
                  }
                </button>
              }
            }
          </li>
        }
      </ul>
    </nav>
  `,
  styles: []
})
export class MenuComponent {
  items = input<MenuItem[]>([]);
  size = input<ComponentSize>('md');
  variant = input<'vertical' | 'horizontal'>('vertical');

  itemClickEvent = output<MenuItem>();

  openSubmenus = signal<Set<string>>(new Set());

  isSubmenuOpen(item: MenuItem): boolean {
    return this.openSubmenus().has(item.label);
  }

  toggleSubmenu(item: MenuItem): void {
    const current = new Set(this.openSubmenus());
    if (current.has(item.label)) {
      current.delete(item.label);
    } else {
      current.add(item.label);
    }
    this.openSubmenus.set(current);
  }

  onItemClick(item: MenuItem): void {
    if (!item.disabled) {
      this.itemClickEvent.emit(item);
    }
  }

  get menuClasses(): string {
    const classes = ['w-full'];
    if (this.variant() === 'horizontal') {
      classes.push('flex', 'items-center', 'space-x-1');
    }
    return classes.join(' ');
  }

  getMenuItemClasses(item: MenuItem): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
      xl: 'px-6 py-4 text-xl'
    };

    const classes = [
      'flex',
      'items-center',
      'gap-3',
      'w-full',
      'rounded-md',
      'transition-colors',
      'duration-150',
      sizeMap[this.size()]
    ];

    if (item.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    classes.push('text-gray-700', 'dark:text-gray-300');

    return classes.join(' ');
  }

  getSubmenuItemClasses(item: MenuItem): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-2.5 text-base',
      xl: 'px-6 py-3 text-lg'
    };

    const classes = [
      'block',
      'rounded-md',
      'transition-colors',
      'duration-150',
      sizeMap[this.size()]
    ];

    if (item.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700');
    }

    classes.push('text-gray-600', 'dark:text-gray-400');

    return classes.join(' ');
  }
}

